import { useDraggable, useDroppable } from "@dnd-kit/core";
import clsx from "clsx";
import { createContext, useCallback, useContext } from "react";
import type { ReactNode } from "react";

import type { FileManagerItem } from "@/components/core/FileManager";
import { useFileManager } from "@/components/core/FileManager/FileManagerContext/FileManagerContext.ts";

import styles from "./FileManagerItemDndWrapper.module.scss";

interface FileManagerItemDndState {
  isDragging: boolean;
  isOver: boolean;
}

const FileManagerItemDndContext = createContext<FileManagerItemDndState>({
  isDragging: false,
  isOver: false,
});

export function useFileManagerItemDnd(): FileManagerItemDndState {
  return useContext(FileManagerItemDndContext);
}

interface FileManagerItemDndWrapperProps {
  item: FileManagerItem;
  children: ReactNode;
  isOsDragOver?: boolean;
  idPrefix?: string;
}

export function FileManagerItemDndWrapper({
  item,
  children,
  isOsDragOver,
  idPrefix = "",
}: FileManagerItemDndWrapperProps) {
  const { isLocked } = useFileManager();

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `${idPrefix}${item.id}`,
    data: { item },
    disabled: isLocked(item, "move"),
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `${idPrefix}drop-${item.id}`,
    data: { item },
    disabled: item.type !== "FOLDER" || isLocked(item, "add"),
  });

  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      setDragRef(node);
      if (item.type === "FOLDER") setDropRef(node);
    },
    [setDragRef, setDropRef, item.type],
  );

  return (
    <FileManagerItemDndContext.Provider value={{ isDragging, isOver }}>
      <div
        ref={setNodeRef}
        className={clsx(
          isDragging && styles.dragging,
          (isOver || isOsDragOver) && styles.dropTarget,
        )}
        data-item-id={item.id}
        data-item-type={item.type}
        {...listeners}
        {...attributes}
      >
        {children}
      </div>
    </FileManagerItemDndContext.Provider>
  );
}
